/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {TrialRegistration} from 'app/modules/auth/components/TrialRegistration'
import PublishedTrustCenter from 'app/pages/trust/PublishedTrustCenter'
import {TrustCenterRegistration} from 'app/modules/auth/components/TrustCenterRegistation'
import { RegistrationLoading } from 'app/components/RegistrationLoading'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  const role = currentUser?.role || ''
  const isOnboardingComplete = currentUser?.onboardingComplete || false
  const isNewUser = currentUser?.isNewUser || false
  const isNewClient = currentUser?.isNewClient || false

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            <>
              {role === 'Member' ? (
                <Route index element={<Navigate to='/training' />} />
              ) : role === 'Auditor' ? (
                <Route index element={<Navigate to='/frameworks' />} />
              ) : isNewClient ? (
                <Route index element={<RegistrationLoading />} />
              ) : isNewUser && !isOnboardingComplete ? (
                <Route index element={<Navigate to='/onboarding' />} />
              ) : currentUser?.account_type === 'trust' ? (
                <Route index element={<Navigate to='/trust-center' />} />
              ) : (
                <>
                  <Route index element={<Navigate to='/dashboard' />} />
                </>
              )}
              <Route path='loading' element={<RegistrationLoading />} />
              <Route path='/*' element={<PrivateRoutes />} />
            </>
          ) : (
            <>
              <Route path='trial' element={<TrialRegistration />} />
              <Route path='trust-center/registration' element={<TrustCenterRegistration />} />
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='loading' element={<RegistrationLoading />} />
              <Route path='/trust/:companyId' element={<PublishedTrustCenter />} />
              <Route path='/trust/:companyId/controls' element={<PublishedTrustCenter />} />
              <Route path='/trust/:companyId/resources' element={<PublishedTrustCenter />} />
              <Route path='/trust/:companyId/subprocessors' element={<PublishedTrustCenter />} />
              <Route path='/trust/:companyId/faq' element={<PublishedTrustCenter />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
